import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { Card } from '@/components/ui/Card';
import { Select } from '@/components/ui/Select';
import { Badge } from '@/components/ui/Badge';
import { useEmailsByDomain, useEmailTemplates } from '@/features/emails/api';
import type { EmailLogStatus } from '@honeycomb/shared';

export const Route = createFileRoute('/_authed/emails/delivery')({
  component: EmailDeliveryPage,
});

type DaysOption = '1' | '7' | '30' | '90';

const DAYS_OPTIONS: { value: DaysOption; label: string }[] = [
  { value: '1', label: 'Last 1 day' },
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
];

function statusTone(status: EmailLogStatus): 'honey' | 'neutral' | 'sage' {
  if (status === 'bounced' || status === 'complained' || status === 'failed') return 'honey';
  if (status === 'sent' || status === 'delivered') return 'sage';
  return 'neutral';
}

function EmailDeliveryPage() {
  const [days, setDays] = useState<DaysOption>('7');
  const [templateId, setTemplateId] = useState<string>('');

  const { data: templatesData } = useEmailTemplates();
  const templates = templatesData?.templates ?? [];

  const { data, isLoading, isError, error } = useEmailsByDomain({
    days: Number(days),
    templateId: templateId || undefined,
  });

  const buckets = data?.buckets ?? [];

  // Derive sorted unique domains (by total count desc) and unique statuses present.
  const domainTotals = new Map<string, number>();
  const statusSet = new Set<string>();
  for (const b of buckets) {
    domainTotals.set(b.domain, (domainTotals.get(b.domain) ?? 0) + b.count);
    statusSet.add(b.status);
  }
  const domains = [...domainTotals.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([domain]) => domain);
  const statuses = [...statusSet] as EmailLogStatus[];

  // Build a lookup map: (domain, status) → count
  const bucketMap = new Map<string, number>();
  for (const b of buckets) {
    bucketMap.set(`${b.domain}::${b.status}`, b.count);
  }

  return (
    <div
      className="p-4 sm:p-6 lg:p-8"
      data-testid="emails-delivery-page"
    >
      <header className="mb-6">
        <h1 className="font-serif text-3xl leading-tight">Email delivery</h1>
        <p className="text-ink-soft text-sm mt-1">
          Delivery breakdown grouped by recipient domain and status.
        </p>
      </header>

      {/* Controls */}
      <div className="flex flex-wrap gap-4 mb-6 items-end">
        <div className="w-48">
          <Select
            label="Date range"
            value={days}
            onChange={(e) => setDays(e.target.value as DaysOption)}
            data-testid="emails-delivery-days-select"
          >
            {DAYS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </div>

        <div className="w-64">
          <Select
            label="Template"
            value={templateId}
            onChange={(e) => setTemplateId(e.target.value)}
            data-testid="emails-delivery-template-select"
          >
            <option value="">All templates</option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.id}
              </option>
            ))}
          </Select>
        </div>
      </div>

      {/* Matrix table */}
      <Card shadow="sm" className="p-0 overflow-hidden overflow-x-auto">
        {isLoading ? (
          <div className="p-6 text-ink-soft">Loading…</div>
        ) : isError ? (
          <div className="p-6 text-ink-soft">
            Failed to load delivery data
            {error instanceof Error ? `: ${error.message}` : '.'}
          </div>
        ) : domains.length === 0 ? (
          <div className="p-6 text-ink-faint" data-testid="emails-delivery-empty">
            No email log rows for this range.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr
                className="text-left text-xs uppercase tracking-wide text-ink-faint border-b"
                style={{ borderColor: 'var(--color-hairline)' }}
              >
                <th className="px-4 py-3">Domain</th>
                {statuses.map((s) => (
                  <th key={s} className="px-4 py-3 text-right">
                    <Badge tone={statusTone(s)}>{s}</Badge>
                  </th>
                ))}
                <th className="px-4 py-3 text-right text-ink-faint">Total</th>
              </tr>
            </thead>
            <tbody>
              {domains.map((domain) => (
                <tr
                  key={domain}
                  className="border-b last:border-b-0"
                  style={{ borderColor: 'var(--color-hairline)' }}
                  data-testid={`emails-delivery-row-${domain}`}
                >
                  <td className="px-4 py-3 font-mono text-xs">{domain}</td>
                  {statuses.map((s) => {
                    const count = bucketMap.get(`${domain}::${s}`) ?? 0;
                    return (
                      <td
                        key={s}
                        className="px-4 py-3 text-right font-mono tabular-nums"
                        data-testid={`emails-delivery-cell-${domain}-${s}`}
                      >
                        {count > 0 ? count : <span className="text-ink-faint">—</span>}
                      </td>
                    );
                  })}
                  <td className="px-4 py-3 text-right font-mono tabular-nums text-ink-soft">
                    {domainTotals.get(domain) ?? 0}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
